import { useMemo } from 'react';
import apiClient from 'src/api/axiosClient';
import { CustomAxiosRequestConfig } from 'src/api/axiosClient/types';
import { Campaign, CampaignAnalytics, CampaignListItem, GetCampaignsParams, GetSurveyRepliesParams, GetSurveysParams, SurveyDetails, SurveyExportFormat, SurveyListItem, SurveyReply, TextToWinWinners } from '../types';
import { AwayMessageCampaignRequestData } from '../types/AwayMessageCampaignRequestData';

type SurveyDateIntervalParams = {
  startDateInterval?: string | null;
  endDateInterval?: string | null;
};

const getSurveyDateIntervalParams = (params?: SurveyDateIntervalParams) => ({
  startDateInterval: params?.startDateInterval || undefined,
  endDateInterval: params?.endDateInterval || undefined,
});

export function useCampaignsApi() {
  return useMemo(
    () => ({
      getCampaigns: (params: GetCampaignsParams) => {
        return apiClient.get<{ items: CampaignListItem[]; count: number }>(`/campaigns`, { params });
      },

      getCampaign: (companyId: string, campaignId: string, config?: CustomAxiosRequestConfig) => {
        return apiClient.get<Campaign>(`/campaigns/${campaignId}`, { ...config, params: { companyId } });
      },

      getCampaignAnalytics: (companyId: string, campaignId: string) => {
        return apiClient.get<CampaignAnalytics>(`/campaigns/${campaignId}/analytics`, { params: { companyId } });
      },

      getTextToWinWinners: (companyId: string, campaignId: string) => {
        return apiClient.get<TextToWinWinners>(`/campaigns/${campaignId}/winners`, { params: { companyId } });
      },

      activateCampaign: (companyId: string, data: string[]) => {
        return apiClient.put<void>(`/campaigns/activate`, data, { params: { companyId } });
      },

      deactivateCampaign: (companyId: string, data: string[]) => {
        return apiClient.put<void>('/campaigns/deactivate', data, { params: { companyId } });
      },

      startCampaign: (companyId: string, campaignId: string, contactIds: string[]) => {
        return apiClient.post<void>(`/campaigns/${campaignId}/start`, contactIds, {
          params: { companyId },
        });
      },

      createAwayMessageCampaign: async (
        companyId: string,
        data: AwayMessageCampaignRequestData,
      ): Promise<string> => {
        const result = await apiClient.post<Campaign>('/campaigns', data, {
          params: { companyId },
        });
        return result.data.id;
      },
      updateAwayMessageCampaign: (
        companyId: string,
        campaignId: string,
        data: AwayMessageCampaignRequestData,
      ) => {
        return apiClient.put<Campaign>(`/campaigns/${campaignId}`, data, { params: { companyId } });
      },
      
      getSurveyMessageReplies: (params: GetSurveyRepliesParams) => {
        return apiClient.get<{ items: SurveyReply[]; count: number }>(`/campaigns/survey-message-replies`, { params });
      },

      searchSurveys: (params: GetSurveysParams) => {
        return apiClient.get<{ items: SurveyListItem[]; count: number }>(`/campaigns/surveys`, { params });
      },

      getSurveyExport: (
        campaignId: string,
        companyId: string,
        format: SurveyExportFormat,
        intervalParams?: SurveyDateIntervalParams,
      ) => {
        return apiClient.get(`/campaigns/${campaignId}/survey/export`, {
          params: { companyId, format, ...getSurveyDateIntervalParams(intervalParams) },
          responseType: 'blob',
        });
      },

      getSurvey: (campaignId: string, companyId: string, intervalParams?: SurveyDateIntervalParams) => {
        return apiClient.get<SurveyDetails>(`/campaigns/${campaignId}/survey`, {
          params: { companyId, ...getSurveyDateIntervalParams(intervalParams) },
        });
      },

      getSurveyMessageRepliesExport: (
        companyId: string,
        scheduledMessageId: string,
        format: SurveyExportFormat,
        intervalParams?: SurveyDateIntervalParams,
      ) => {
        return apiClient.get('/campaigns/survey-message-replies/export', {
          params: {
            companyId,
            scheduledMessageId,
            format,
            ...getSurveyDateIntervalParams(intervalParams),
          },
          responseType: 'blob',
        });
      },
    }),
    [],
  );
}
